import { useState, useEffect } from 'react';
import Skeleton from '@carvana/ds-skeleton';
import { getUserState } from '@carvana/core-auth';

import styles from '../HeroLoadingFallback.module.css';
import HeroLoadingFallback from '../HeroLoadingFallback';
import VehicleEntry from '../VehicleEntry';
import { StyledCard } from '../VehicleEntry/Entry.styles';
import SummaryTiles from './SummaryTiles';
import {
  Background,
  Heading,
  HeadingWrapper,
  HeroContainer,
  HeroFoldContainer,
  SubTitle
} from './Hero.styles';
import { headerCopy, subHeaderCopy } from './Hero.content';
import { getHeroHeading } from './getHeroHeading';
import useGetSummaries from 'shared/hooks/useGetSummaries';

const Hero = () => {
  const [hasMounted, setHasMounted] = useState(false);

  const {
    user: { given_name: givenName }
  } = getUserState();

  const { data, isPending } = useGetSummaries();
  const summaries = data?.vehicleSummaries;
  const showEntryWidget = !summaries || summaries.length === 0;
  const heading = showEntryWidget
    ? headerCopy
    : getHeroHeading(givenName, summaries?.[0]?.lifecycleState);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // A persistent summaries-fetch failure resolves data to undefined rather than
  // throwing (see useGetSummaries) — showEntryWidget above already treats that
  // the same as "no summaries", so no separate error branch is needed here.
  // The only state that needs its own render is "still fetching," previously
  // handled by the Suspense boundary this hook no longer participates in.
  if (isPending) return <HeroLoadingFallback />;

  const renderRightColumn = () => {
    if (showEntryWidget)
      return (
        <StyledCard>
          <VehicleEntry />
        </StyledCard>
      );
    return <SummaryTiles summaries={summaries!} />;
  };

  return (
    <Background>
      <HeroContainer>
        <HeroFoldContainer>
          <HeadingWrapper>
            {hasMounted ? (
              <>
                <Heading>{heading}</Heading>
                <SubTitle style={{ whiteSpace: 'pre-wrap' }}>{subHeaderCopy}</SubTitle>
              </>
            ) : (
              <>
                <Heading as="div" className={styles.relative}>
                  <span className={styles.hidden} aria-hidden="true">
                    {heading}
                  </span>
                  <div className={styles.absoluteOverlay}>
                    <Skeleton width="100%" height="100%" />
                  </div>
                </Heading>
                <SubTitle className={styles.relative}>
                  <span className={styles.hiddenPreWrap} aria-hidden="true">
                    {subHeaderCopy}
                  </span>
                  <div className={`${styles.absoluteOverlay} ${styles.flexColumn}`}>
                    <Skeleton width="100%" height="24px" />
                    <Skeleton width="65%" height="24px" />
                  </div>
                </SubTitle>
              </>
            )}
          </HeadingWrapper>
          {renderRightColumn()}
        </HeroFoldContainer>
      </HeroContainer>
    </Background>
  );
};

export default Hero;
